/**
 * ChantierSection - Composant principal qui route vers le bon module
 * selon le type de chantier (Rénovation Globale vs CEE Fiches Standardisées)
 * 
 * RÈGLE: Le type de chantier détermine automatiquement la trame affichée
 * - prospect_class === 'renovation' → RenovationGlobaleModule
 * - prospect_class === 'agri' ou 'tertiaire' → CEEFichesStandardiseesModule
 */
import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { HardHat, Save, Loader2, Building2, User, MapPin, FileText, Calendar, Trash2, Upload, Eye, Plus, X, Wrench, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { prospectsAPI, searchAPI } from '@/lib/api';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { PDFPreviewButton } from '@/components/PDFPreviewModal';

// Import des nouveaux modules
import { RenovationGlobaleModule } from '@/components/chantier/RenovationGlobaleModule';
import { CEEFichesStandardiseesModule } from '@/components/chantier/CEEFichesStandardiseesModule';

// Secteurs d'activité (pour le formulaire classique)
const SECTEURS = [
    { value: 'agriculture', label: 'Agriculture' },
    { value: 'industrie', label: 'Industrie' },
    { value: 'tertiaire', label: 'Tertiaire' },
    { value: 'commerce', label: 'Commerce' },
    { value: 'artisanat', label: 'Artisanat' },
    { value: 'hotellerie', label: 'Hôtellerie / Restauration' },
    { value: 'sante', label: 'Santé' },
    { value: 'collectivite', label: 'Collectivité' },
    { value: 'autre', label: 'Autre' }
];

/**
 * Détermine le type de parcours chantier basé sur la classe du prospect
 */
function getChantierType(prospectClass) {
    if (prospectClass === 'renovation') {
        return 'renovation_globale';
    } else if (prospectClass === 'agri' || prospectClass === 'tertiaire') {
        return 'cee_standardisees';
    }
    return 'legacy'; // Ancien système pour les autres cas
}

export function ChantierSection({ prospect, onUpdate }) {
    const chantierType = getChantierType(prospect?.prospect_class);
    
    // Si c'est un parcours Rénovation Globale
    if (chantierType === 'renovation_globale') {
        return <RenovationGlobaleModule prospect={prospect} onUpdate={onUpdate} />;
    }
    
    // Si c'est un parcours CEE Fiches Standardisées
    if (chantierType === 'cee_standardisees') {
        return (
            <div className="space-y-6">
                <CEEFichesStandardiseesModule prospect={prospect} onUpdate={onUpdate} />
                {/* Garder les infos de base et pièces jointes en dessous */}
                <LegacyChantierForm prospect={prospect} onUpdate={onUpdate} />
            </div>
        );
    }
    
    // Sinon, afficher le formulaire legacy
    return <LegacyChantierForm prospect={prospect} onUpdate={onUpdate} />;
}

/**
 * Formulaire chantier legacy (ancien système)
 * Conservé pour la rétrocompatibilité et les cas non couverts
 */
function LegacyChantierForm({ prospect, onUpdate }) {
    const [saving, setSaving] = useState(false);
    const [citiesForPostalCode, setCitiesForPostalCode] = useState([]);
    const [loadingCities, setLoadingCities] = useState(false);
    const citySearchTimeoutRef = useRef(null);
    
    // États pour les PJ Chantier
    const [uploadingFile, setUploadingFile] = useState(false);
    const fileInputRef = useRef(null);
    
    // Données Chantier
    const [formData, setFormData] = useState({
        // Informations client
        raison_sociale: '',
        siren: '',
        secteur: '',
        // Contact
        contact: {
            nom: '',
            prenom: '',
            email: '',
            fonction: '',
            telephone: ''
        },
        // Adresse du chantier
        adresse_chantier: '',
        code_postal_chantier: '',
        ville_chantier: '',
        // Détails chantier
        description: '',
        date_debut_prevue: '',
        date_fin_prevue: '',
        date_fin_reelle: '',
        // Notes
        notes: ''
    });

    // Charger les données existantes
    useEffect(() => {
        if (prospect?.chantier_data) {
            setFormData(prev => ({
                ...prev,
                ...prospect.chantier_data,
                contact: { ...prev.contact, ...(prospect.chantier_data.contact || {}) }
            }));
        }
        // Pré-remplir depuis les infos prospect si chantier_data vide
        if (!prospect?.chantier_data && prospect) {
            setFormData(prev => ({
                ...prev,
                raison_sociale: prospect.company_name || '',
                contact: {
                    nom: prospect.last_name || '',
                    prenom: prospect.first_name || '',
                    email: prospect.email || '',
                    telephone: prospect.phone || '',
                    fonction: ''
                }
            }));
        }
    }, [prospect]);

    // Recherche ville par code postal
    const searchCitiesByPostalCode = async (postalCode) => {
        if (!postalCode || postalCode.length < 5) {
            setCitiesForPostalCode([]);
            return;
        }
        
        setLoadingCities(true);
        try {
            const response = await searchAPI.cities(postalCode);
            setCitiesForPostalCode(response.data || []);
        } catch (error) {
            console.error('Erreur recherche ville:', error);
        } finally {
            setLoadingCities(false);
        }
    };

    const handlePostalCodeChange = (value) => {
        setFormData(prev => ({ ...prev, code_postal_chantier: value }));
        
        if (citySearchTimeoutRef.current) {
            clearTimeout(citySearchTimeoutRef.current);
        }
        
        citySearchTimeoutRef.current = setTimeout(() => {
            searchCitiesByPostalCode(value);
        }, 300);
    };

    const handleCitySelect = (city) => {
        setFormData(prev => ({
            ...prev,
            ville_chantier: city.name,
            code_postal_chantier: city.postal_code
        }));
        setCitiesForPostalCode([]);
    };

    const handleInputChange = (field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: { ...prev[parent], [child]: value }
            }));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    // Sauvegarder les données Chantier
    const handleSave = async () => {
        setSaving(true);
        try {
            await prospectsAPI.update(prospect.id, {
                chantier_data: formData
            });
            toast.success('Informations Chantier sauvegardées');
            onUpdate?.();
        } catch (error) {
            console.error('Erreur sauvegarde:', error);
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    // Upload PJ Chantier
    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        setUploadingFile(true);
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('category', 'chantier');
        
        try {
            await prospectsAPI.uploadAttachment(prospect.id, formDataUpload);
            toast.success('Fichier uploadé');
            onUpdate?.();
        } catch (error) {
            console.error('Erreur upload:', error);
            toast.error('Erreur lors de l\'upload');
        } finally {
            setUploadingFile(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Supprimer PJ Chantier
    const handleDeleteAttachment = async (attachmentId) => {
        if (!confirm('Supprimer ce fichier ?')) return;
        
        try {
            await prospectsAPI.deleteAttachment(prospect.id, attachmentId);
            toast.success('Fichier supprimé');
            onUpdate?.();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    // Filtrer les PJ de type Chantier
    const chantierAttachments = (prospect?.attachments || []).filter(
        att => att.category === 'chantier'
    );

    return (
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50/50 to-white">
            <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-100">
                            <Wrench className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <CardTitle className="text-lg text-blue-900">Informations Chantier</CardTitle>
                            <CardDescription>Données complémentaires et documents</CardDescription>
                        </div>
                    </div>
                    <Button onClick={handleSave} disabled={saving} size="sm">
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Sauvegarder
                    </Button>
                </div>
            </CardHeader>
            
            <CardContent className="space-y-6">
                {/* SECTION: Informations Client */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
                        <Building2 className="w-4 h-4" />
                        Informations Client
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label>Raison sociale</Label>
                            <Input
                                value={formData.raison_sociale}
                                onChange={(e) => handleInputChange('raison_sociale', e.target.value)}
                                placeholder="Nom de l'entreprise"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>SIREN</Label>
                            <Input
                                value={formData.siren}
                                onChange={(e) => handleInputChange('siren', e.target.value)}
                                placeholder="123 456 789"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Secteur d'activité</Label>
                            <Select
                                value={formData.secteur}
                                onValueChange={(v) => handleInputChange('secteur', v)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Sélectionner" />
                                </SelectTrigger>
                                <SelectContent>
                                    {SECTEURS.map(s => (
                                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
                
                <Separator />
                
                {/* SECTION: Contact */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
                        <User className="w-4 h-4" />
                        Contact Chantier
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label>Prénom</Label>
                            <Input
                                value={formData.contact.prenom}
                                onChange={(e) => handleInputChange('contact.prenom', e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Nom</Label>
                            <Input
                                value={formData.contact.nom}
                                onChange={(e) => handleInputChange('contact.nom', e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Fonction</Label>
                            <Input
                                value={formData.contact.fonction}
                                onChange={(e) => handleInputChange('contact.fonction', e.target.value)}
                                placeholder="Directeur, Gérant..."
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Email</Label>
                            <Input
                                type="email"
                                value={formData.contact.email}
                                onChange={(e) => handleInputChange('contact.email', e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Téléphone</Label>
                            <Input
                                value={formData.contact.telephone}
                                onChange={(e) => handleInputChange('contact.telephone', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
                
                <Separator />
                
                {/* SECTION: Adresse Chantier */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
                        <MapPin className="w-4 h-4" />
                        Adresse du Chantier
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2 space-y-2">
                            <Label>Adresse</Label>
                            <Input
                                value={formData.adresse_chantier}
                                onChange={(e) => handleInputChange('adresse_chantier', e.target.value)}
                                placeholder="Numéro et rue"
                            />
                        </div>
                        <div className="space-y-2 relative">
                            <Label>Code postal</Label>
                            <Input
                                value={formData.code_postal_chantier}
                                onChange={(e) => handlePostalCodeChange(e.target.value)}
                                placeholder="75001"
                                maxLength={5}
                            />
                            {loadingCities && (
                                <div className="absolute right-3 top-9">
                                    <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                                </div>
                            )}
                            {citiesForPostalCode.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-40 overflow-auto">
                                    {citiesForPostalCode.map((city, idx) => (
                                        <div
                                            key={idx}
                                            className="px-3 py-2 hover:bg-slate-100 cursor-pointer text-sm"
                                            onClick={() => handleCitySelect(city)}
                                        >
                                            {city.name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label>Ville</Label>
                            <Input
                                value={formData.ville_chantier}
                                onChange={(e) => handleInputChange('ville_chantier', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
                
                <Separator />
                
                {/* SECTION: Détails Chantier */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
                        <FileText className="w-4 h-4" />
                        Détails du Chantier
                    </div>
                    
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Description du chantier</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                                placeholder="Description des travaux, type de chantier..."
                                rows={3}
                            />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    Date début prévue
                                </Label>
                                <Input
                                    type="date"
                                    value={formData.date_debut_prevue}
                                    onChange={(e) => handleInputChange('date_debut_prevue', e.target.value)}
                                    data-testid="ch-date-debut"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    Date fin prévue
                                </Label>
                                <Input
                                    type="date"
                                    value={formData.date_fin_prevue}
                                    onChange={(e) => handleInputChange('date_fin_prevue', e.target.value)}
                                    data-testid="ch-date-fin-prevue"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    Date fin réelle
                                </Label>
                                <Input
                                    type="date"
                                    value={formData.date_fin_reelle}
                                    onChange={(e) => handleInputChange('date_fin_reelle', e.target.value)}
                                    data-testid="ch-date-fin-reelle"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                
                <Separator />
                
                {/* SECTION: Notes */}
                <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
                        <FileText className="w-4 h-4" />
                        Observations / Notes
                    </div>
                    
                    <Textarea
                        value={formData.notes}
                        onChange={(e) => handleInputChange('notes', e.target.value)}
                        placeholder="Notes, observations, remarques..."
                        rows={4}
                    />
                </div>
                
                <Separator />
                
                {/* SECTION: Pièces Jointes Chantier */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
                            <FileText className="w-4 h-4" />
                            Pièces Jointes Chantier
                        </div>
                        <div>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                className="hidden"
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                            />
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadingFile}
                            >
                                {uploadingFile ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Upload className="w-4 h-4 mr-2" />
                                )}
                                Ajouter un fichier
                            </Button>
                        </div>
                    </div>
                    
                    {chantierAttachments.length === 0 ? (
                        <div className="text-center py-8 text-slate-400 bg-slate-50 rounded-lg border-2 border-dashed">
                            <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p className="text-sm">Aucune pièce jointe chantier</p>
                            <p className="text-xs mt-1">Cliquez sur "Ajouter un fichier" pour uploader</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {chantierAttachments.map((att) => (
                                <div
                                    key={att.id}
                                    className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-slate-50"
                                >
                                    <div className="flex items-center gap-3">
                                        <FileText className="w-5 h-5 text-blue-500" />
                                        <div>
                                            <p className="text-sm font-medium">{att.original_filename}</p>
                                            <p className="text-xs text-slate-400">
                                                {att.uploaded_at ? new Date(att.uploaded_at).toLocaleDateString('fr-FR') : ''}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <PDFPreviewButton
                                            attachmentId={att.id}
                                            filename={att.original_filename}
                                            prospectId={prospect.id}
                                        />
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                            onClick={() => handleDeleteAttachment(att.id)}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

export default ChantierSection;
